type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "warn">;

export const ACTIVE_PLATFORM_PORT_STATE_KEY = "active_platform_port";

export type PlatformSlotActivity = {
  active: boolean;
  currentPort: string | null;
  activePort: string | null;
  reason: "active" | "inactive" | "unconfigured" | "check_failed";
};

type ActivePortRow = {
  value: string | null;
};

type NodeActivePortRow = {
  active_port: string | number | null;
};

function rowsFromUnknown<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  return ((result as { rows?: T[] } | null)?.rows ?? []) as T[];
}

export function normalizePlatformPort(port: string | number | null | undefined): string | null {
  if (port == null) return null;
  const normalized = String(port).trim();
  return normalized.length > 0 ? normalized : null;
}

export function getCurrentPlatformPort(env?: { PORT?: string | undefined }): string | null {
  const source = env ?? (process.env as Record<string, string | undefined>);
  return normalizePlatformPort(source.PORT);
}

export function normalizePlatformNodeId(nodeId: string | null | undefined): string | null {
  if (nodeId == null) return null;
  const normalized = String(nodeId).trim();
  return normalized.length > 0 ? normalized : null;
}

export function getCurrentPlatformNodeId(env?: { GENCOW_NODE_ID?: string | undefined }): string | null {
  const source = env ?? (process.env as Record<string, string | undefined>);
  return normalizePlatformNodeId(source.GENCOW_NODE_ID);
}

export async function ensurePlatformRuntimeStateTable(rawSql: RawSql): Promise<void> {
  await rawSql(`CREATE TABLE IF NOT EXISTS platform_runtime_state (
            key TEXT PRIMARY KEY,
            value TEXT NOT NULL,
            updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
        )`);
}

export async function setActivePlatformPort(rawSql: RawSql, port: string | number): Promise<void> {
  const normalizedPort = normalizePlatformPort(port);
  if (!normalizedPort) throw new Error("active platform port is required");
  await ensurePlatformRuntimeStateTable(rawSql);
  await rawSql(
    `INSERT INTO platform_runtime_state (key, value, updated_at)
         VALUES ($1, $2, NOW())
         ON CONFLICT (key) DO UPDATE SET value = EXCLUDED.value, updated_at = NOW()`,
    [ACTIVE_PLATFORM_PORT_STATE_KEY, normalizedPort],
  );
}

export async function getActivePlatformPort(rawSql: RawSql): Promise<string | null> {
  const rows = rowsFromUnknown<ActivePortRow>(
    await rawSql(`SELECT value FROM platform_runtime_state WHERE key = $1 LIMIT 1`, [
      ACTIVE_PLATFORM_PORT_STATE_KEY,
    ]),
  );
  return normalizePlatformPort(rows[0]?.value);
}

export async function getNodeActivePlatformPort(rawSql: RawSql, nodeId: string): Promise<string | null> {
  const rows = rowsFromUnknown<NodeActivePortRow>(
    await rawSql(`SELECT active_port FROM platform_nodes WHERE id = $1 LIMIT 1`, [nodeId]),
  );
  return normalizePlatformPort(rows[0]?.active_port);
}

export async function getPlatformSlotActivity(params: {
  rawSql: RawSql;
  currentPort?: string | number | null;
  nodeId?: string | null;
  env?: { PORT?: string | undefined; GENCOW_NODE_ID?: string | undefined };
  logger?: LoggerLike;
}): Promise<PlatformSlotActivity> {
  const currentPort = normalizePlatformPort(params.currentPort ?? getCurrentPlatformPort(params.env));
  const nodeId = normalizePlatformNodeId(params.nodeId ?? getCurrentPlatformNodeId(params.env));
  try {
    let activePort: string | null = null;
    if (nodeId) {
      try {
        activePort = await getNodeActivePlatformPort(params.rawSql, nodeId);
      } catch {
        activePort = null;
      }
    }
    activePort ??= await getActivePlatformPort(params.rawSql);
    if (!currentPort || !activePort) {
      return { active: true, currentPort, activePort, reason: "unconfigured" };
    }
    if (currentPort === activePort) {
      return { active: true, currentPort, activePort, reason: "active" };
    }
    return { active: false, currentPort, activePort, reason: "inactive" };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    params.logger?.warn(`[platform] active-slot check failed; treating current slot as active: ${message}`);
    return { active: true, currentPort, activePort: null, reason: "check_failed" };
  }
}
